import React, { useEffect, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import Menu from './Menu';

const defaultOf = (filter) => filter.defaultValue ?? '';
const isActive = (filter, values) => (values[filter.key] ?? defaultOf(filter)) !== defaultOf(filter);
const optionLabel = (filter, value) => filter.options?.find((o) => o.value === value)?.label ?? value;
// Focus ring on the wrapper while its input has focus (not while the clear button has).
export const INPUT_RING = 'has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-signal/40';

function SearchFilter({ filter, value, onCommit }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const dirty = draft.trim() !== value;

  return (
    <label
      className={`flex items-center gap-2 h-8 pl-2.5 pr-1 rounded-control border bg-sheet focus-within:border-signal ${INPUT_RING} ${value ? 'border-signal' : 'border-rule'}`}
      style={{ width: filter.width || 240 }}
    >
      <span className="shrink-0 text-graphite">{filter.label}</span>
      <input
        type="text"
        value={draft}
        placeholder={filter.placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onCommit(draft.trim());
          if (event.key === 'Escape') setDraft(value);
        }}
        className={`flex-1 min-w-0 h-full bg-transparent border-0 p-0 outline-none text-ink placeholder:text-graphite/70 ${filter.mono ? 'font-mono text-id' : 'text-body'}`}
      />
      {dirty ? (
        <kbd className="shrink-0 mr-1 px-1.5 rounded-badge border border-rule font-mono text-small text-graphite" title="Press Enter to apply">
          ↵
        </kbd>
      ) : (
        value && (
          <button
            type="button"
            onClick={() => onCommit('')}
            className="shrink-0 p-1 rounded-control text-graphite hover:bg-hover hover:text-ink"
            aria-label={`Clear ${filter.label} filter`}
          >
            <X size={14} aria-hidden="true" />
          </button>
        )
      )}
    </label>
  );
}

function DateFilter({ filter, value, onCommit }) {
  return (
    <label className={`flex items-center gap-2 h-8 pl-2.5 pr-1.5 rounded-control border bg-sheet focus-within:border-signal focus-within:ring-2 focus-within:ring-signal/40 ${value ? 'border-signal' : 'border-rule'}`}>
      <span className="shrink-0 text-graphite">{filter.label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onCommit(event.target.value)}
        className="h-full bg-transparent border-0 p-0 outline-none text-body text-ink tabular-nums"
      />
    </label>
  );
}

function SelectFilter({ filter, value, onCommit }) {
  const active = value !== defaultOf(filter);
  const items = filter.options.map((option) => ({
    key: option.value || 'default',
    label: option.label,
    type: 'radio',
    checked: option.value === value,
    onSelect: () => onCommit(option.value),
  }));

  return (
    <Menu items={items} label={`${filter.label} filter`} triggerClassName={active ? 'border-signal' : ''}>
      <span className="text-graphite">{filter.label}:</span>
      <span className={active ? 'font-semibold text-ink' : 'text-ink'}>{optionLabel(filter, value)}</span>
      <ChevronDown size={14} className="text-graphite" aria-hidden="true" />
    </Menu>
  );
}

/**
 * Labeled filters with removable chips for the active ones.
 * filters: [{ key, label, type: 'search' | 'select' | 'date', placeholder?, width?, mono?, options?: [{ value, label }], defaultValue?, hidden? }]
 * Search filters apply on Enter; date values are YYYY-MM-DD. onChange receives the full next values object.
 * hidden filters have no control (they arrive through links) but still show a chip when active.
 */
export default function FilterBar({ filters, values, onChange, className = '' }) {
  const active = filters.filter((f) => isActive(f, values));
  const commit = (key, value) => onChange({ ...values, [key]: value });
  const clearAll = () => onChange(Object.fromEntries(filters.map((f) => [f.key, defaultOf(f)])));

  return (
    <div role="search" className={`shrink-0 flex flex-wrap items-center gap-2 ${className}`}>
      {filters.filter((f) => !f.hidden).map((filter) => {
        const value = values[filter.key] ?? defaultOf(filter);
        const Control = { select: SelectFilter, date: DateFilter }[filter.type] || SearchFilter;
        return <Control key={filter.key} filter={filter} value={value} onCommit={(next) => commit(filter.key, next)} />;
      })}

      {active.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 ml-1">
          {active.map((filter) => (
            <span key={filter.key} className="inline-flex items-center gap-1 h-6 pl-2 pr-0.5 rounded-badge bg-signal-soft text-signal text-small">
              <span className="max-w-[220px] truncate">
                {filter.label}: <span className={filter.mono ? 'font-mono' : ''}>{optionLabel(filter, values[filter.key])}</span>
              </span>
              <button
                type="button"
                onClick={() => commit(filter.key, defaultOf(filter))}
                className="p-0.5 rounded-badge hover:bg-signal/15"
                aria-label={`Remove ${filter.label} filter`}
              >
                <X size={12} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </span>
          ))}
          <button type="button" onClick={clearAll} className="ml-1 text-small text-signal hover:underline">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
